import { assembliesApi } from '@/api/assemblies.api';
import type { AssemblyDrawingSpec } from '@/types/assemblyDrawing.types';
import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * Drawing specifications already fetched, kept for the lifetime of the tab.
 *
 * The tools table mounts one thumbnail per row and pages ten rows at a time, so without a
 * cache every trip back to the first page would refetch the same ten sheets. Keyed by
 * assembly id — the key the API route itself takes — and only ever holding what a row
 * asked for, so it stays the size of the pages the user actually visited.
 */
const specCache = new Map<string, AssemblyDrawingSpec>();

/**
 * Assemblies the backend would not draw: 404, 422 (no drawable geometry), or a failed
 * request. Remembered so an undrawable assembly costs one request, not one per page view.
 */
const unavailableIds = new Set<string>();

/** Requests in flight, so two rows pointing at one assembly share a single response. */
const inFlight = new Map<string, Promise<AssemblyDrawingSpec>>();

/**
 * Bumped every time the cache is dropped.
 *
 * A thumbnail keyed only by assembly id would keep showing the old silhouette after an
 * import or a dimension edit, because the id did not change. Subscribing to this counter
 * is what makes those writes refetch.
 */
let cacheGeneration = 0;

const generationListeners = new Set<() => void>();

function subscribeToGeneration(onStoreChange: () => void): () => void {
  generationListeners.add(onStoreChange);
  return () => {
    generationListeners.delete(onStoreChange);
  };
}

function getGeneration(): number {
  return cacheGeneration;
}

/**
 * Forget every cached sheet and refetch the ones currently on screen.
 *
 * Call it after a write that can move the geometry — a tool-database import, or a saved
 * change to a diameter or a length — since the drawing is derived from those numbers.
 */
export function clearAssemblyDrawingCache(): void {
  if (specCache.size === 0 && unavailableIds.size === 0) return;
  specCache.clear();
  unavailableIds.clear();
  cacheGeneration += 1;
  for (const listener of generationListeners) listener();
}

/**
 * Fetch one sheet, sharing the request with any row that asks for the same one.
 *
 * The in-flight key carries the generation as well as the assembly id: a request started
 * before a cache drop then neither satisfies a caller that asked afterwards nor deletes
 * that caller's newer entry when it finally settles.
 */
function loadSpec(assemblyId: string): Promise<AssemblyDrawingSpec> {
  const generationAtStart = cacheGeneration;
  const requestKey = `${generationAtStart}:${assemblyId}`;
  const running = inFlight.get(requestKey);
  if (running) return running;

  const request = assembliesApi
    .getDrawingData(assemblyId)
    .then((spec) => {
      // A drop between request and response means this sheet describes the catalogue as
      // it was before the write, so it must not become the cached answer.
      if (cacheGeneration === generationAtStart) specCache.set(assemblyId, spec);
      return spec;
    })
    .finally(() => {
      inFlight.delete(requestKey);
    });

  inFlight.set(requestKey, request);
  return request;
}

/** What one row's thumbnail has to show right now. */
export type AssemblyThumbnailState =
  | { status: 'empty' }
  | { status: 'loading' }
  | { status: 'ready'; spec: AssemblyDrawingSpec }
  | { status: 'unavailable' };

/** The state the caches alone can answer with; `loading` means a request is still owed. */
function stateFor(assemblyId: string): AssemblyThumbnailState {
  const cached = specCache.get(assemblyId);
  if (cached) return { status: 'ready', spec: cached };
  if (unavailableIds.has(assemblyId)) return { status: 'unavailable' };
  return { status: 'loading' };
}

function sameState(a: AssemblyThumbnailState, b: AssemblyThumbnailState): boolean {
  if (a.status !== b.status) return false;
  if (a.status === 'ready' && b.status === 'ready') return a.spec === b.spec;
  return true;
}

/**
 * The drawing of one assembly, loaded on mount and cached across pages.
 *
 * Pass `null` for a tool that has no assembly. The cache is read during render rather
 * than from the effect, so paging back to an already-seen page paints the silhouettes on
 * the first frame instead of flashing skeletons.
 */
export function useAssemblyThumbnail(assemblyId: string | null): AssemblyThumbnailState {
  const generation = useSyncExternalStore(subscribeToGeneration, getGeneration);
  const [state, setState] = useState<AssemblyThumbnailState>(() =>
    assemblyId ? stateFor(assemblyId) : { status: 'empty' }
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: generation is a cache-drop trigger, not a value read here — bumping it refetches the same assembly.
  useEffect(() => {
    if (!assemblyId) {
      setState((previous) => (previous.status === 'empty' ? previous : { status: 'empty' }));
      return;
    }

    const resolved = stateFor(assemblyId);
    setState((previous) => (sameState(previous, resolved) ? previous : resolved));
    if (resolved.status !== 'loading') return;

    let cancelled = false;
    loadSpec(assemblyId)
      .then((spec) => {
        if (!cancelled) setState({ status: 'ready', spec });
      })
      .catch(() => {
        unavailableIds.add(assemblyId);
        if (!cancelled) setState({ status: 'unavailable' });
      });

    return () => {
      cancelled = true;
    };
  }, [assemblyId, generation]);

  return state;
}
